import React, { Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Navigate, RouteObject, useNavigate, useParams } from "react-router";

import { useQueryParamHide } from "@/features/procedures/hooks/useQueryParamHide";
import EditChecklistPageSkeleton from "@/features/procedures/ui/components/skeletons/EditChecklistPageSkeleton";
import { ProcedureUpdates } from "@/features/task";
import PageError from "@/shared/ui/errors/PageError";
import * as Sentry from "@sentry/react";

const LazyProcedurePage = React.lazy(
	() => import("@/features/procedures/pages/procedure-page/ProcedurePage"),
);
const LazyProcedures = React.lazy(
	() => import("@/features/procedures/ui/components/procedures-list/Procedures"),
);

const ChecklistRouteChooser: React.FC = () => {
	const hide = useQueryParamHide();
	return (
		<Suspense fallback={<EditChecklistPageSkeleton />}>
			{hide ? <LazyProcedurePage /> : <LazyProcedures />}
		</Suspense>
	);
};

// Legacy route — the in-page node editor has moved to ManualChecklistEditorPage.
// Redirect stale bookmarks/emails to the parent procedure instead of 404ing.
const RedirectToProcedure: React.FC = () => {
	const { id } = useParams();
	return <Navigate to={`/procedure/${id}`} replace />;
};

// Heavy procedure pages are lazy-loaded so the procedures bundle loads only when needed.
export const procedureRoutes: RouteObject[] = [
	{
		path: "procedures/*",
		lazy: async () => {
			const [{ default: ProceduresPage }, { default: PageErrorC }] =
				await Promise.all([
					import("@/features/procedures/pages/procedures-page/ProceduresPage").then(
						(m) => ({ default: m.default ?? m.ProceduresPage }),
					),
					import("@/shared/ui/errors/PageError"),
				]);
			return {
				Component: () => (
					<Sentry.ErrorBoundary
						fallback={({ error, componentStack, resetError }) => (
							<PageErrorC
								error={error}
								componentStack={componentStack}
								reset={resetError}
							/>
						)}
					>
						<ProceduresPage />
					</Sentry.ErrorBoundary>
				),
			};
		},
		children: [
			{
				index: true,
				lazy: async () => {
					const [{ default: ProceduresList }, { default: PageErrorC }] =
						await Promise.all([
							import("@/features/procedures/ui/components/procedures-list/Procedures"),
							import("@/shared/ui/errors/PageError"),
						]);
					return {
						Component: () => (
							<Sentry.ErrorBoundary
								fallback={({ error, componentStack, resetError }) => (
									<PageErrorC
										error={error}
										componentStack={componentStack}
										reset={resetError}
									/>
								)}
							>
								<ProceduresList />
							</Sentry.ErrorBoundary>
						),
					};
				},
			},
			{
				path: "procedureUpdates",
				children: [
					{
						path: "newImprov",
						element: <ProcedureUpdates page="newImprov" />,
					},
					{
						path: "myImprov",
						element: <ProcedureUpdates page="myImprov" />,
					},
					{
						path: "verified",
						element: <ProcedureUpdates page="verified" />,
					},
					{
						path: "declined",
						element: <ProcedureUpdates page="declined" />,
					},
				],
			},
			{
				path: "compareProcedures",
				element: <Navigate to="/procedures?tab=procedureReview" replace />,
			},
			{
				path: ":id",
				lazy: async () => {
					const { default: ProcedurePageComponent } =
						await import("@/features/procedures/pages/procedure-page/ProcedurePage");
					return { Component: ProcedurePageComponent };
				},
			},
		],
	},
	{
		path: "procedure/:id/*",
		lazy: async () => {
			const [{ default: ProceduresPage }, { default: PageErrorC }] =
				await Promise.all([
					import("@/features/procedures/pages/procedures-page/ProceduresPage").then(
						(m) => ({ default: m.default ?? m.ProceduresPage }),
					),
					import("@/shared/ui/errors/PageError"),
				]);
			return {
				Component: () => (
					<Sentry.ErrorBoundary
						fallback={({ error, componentStack, resetError }) => (
							<PageErrorC
								error={error}
								componentStack={componentStack}
								reset={resetError}
							/>
						)}
					>
						<ProceduresPage />
					</Sentry.ErrorBoundary>
				),
			};
		},
		children: [
			{
				index: true,
				lazy: async () => {
					const { default: ProcedurePageComponent } =
						await import("@/features/procedures/pages/procedure-page/ProcedurePage");
					return { Component: ProcedurePageComponent };
				},
			},
			{
				path: "checklist/:schemeId",
				Component: ChecklistRouteChooser,
			},
			{
				path: "checklist/:schemeId/edit",
				lazy: async () => {
					const { ManualChecklistEditorPage: EditChecklistPage } =
						await import("@/features/procedures/pages/checklist-editor-page/ChecklistEditorPage");
					const EditChecklistRoute: React.FC = () => {
						const navigate = useNavigate();
						return (
							<ErrorBoundary
								FallbackComponent={({ error, resetErrorBoundary }) => (
									<PageError
										error={error}
										componentStack=""
										reset={resetErrorBoundary}
									/>
								)}
								onReset={() => navigate(-1)}
							>
								<Suspense fallback={<EditChecklistPageSkeleton />}>
									<EditChecklistPage />
								</Suspense>
							</ErrorBoundary>
						);
					};
					return { Component: EditChecklistRoute };
				},
			},
			{
				path: "node/:schemeId/edit",
				Component: RedirectToProcedure,
			},
			{
				path: "node/:groupId/CompareVersions",
				lazy: async () => {
					const [
						{ default: ProcedureComparisonPage },
						{ default: PageErrorC },
					] = await Promise.all([
						import("@/features/procedures/pages/procedure-comparison-page/ProcedureComparisonPage"),
						import("@/shared/ui/errors/PageError"),
					]);
					return {
						Component: () => (
							<Sentry.ErrorBoundary
								fallback={({ error, componentStack, resetError }) => (
									<PageErrorC
										error={error}
										componentStack={componentStack}
										reset={resetError}
									/>
								)}
							>
								<ProcedureComparisonPage />
							</Sentry.ErrorBoundary>
						),
					};
				},
			},
		],
	},
	{
		path: "procedure/:id/nodeDiagram",
		lazy: async () => {
			const [{ default: NodeDiagramPage }, { default: PageErrorC }] =
				await Promise.all([
					import("@/features/procedures/ui/components/node-diagram/NodeDiagramPage"),
					import("@/shared/ui/errors/PageError"),
				]);
			return {
				Component: () => (
					<Sentry.ErrorBoundary
						fallback={({ error, componentStack, resetError }) => (
							<PageErrorC
								error={error}
								componentStack={componentStack}
								reset={resetError}
							/>
						)}
					>
						<NodeDiagramPage />
					</Sentry.ErrorBoundary>
				),
			};
		},
	},
];
